/**
 * core/intelligence/boardHealth.ts
 *
 * Board Health Analysis — derives a health score and a set of human-readable
 * insights from the raw item data on a monday.com board.
 *
 * Health score: 0–100 (100 = perfectly healthy)
 * Deductions:
 *   -2  per item overdue
 *   -5  per Blocked/Stuck item
 *   -3  per Escalated item
 *   -10 flat if > 30 % of items are overdue
 *   -10 flat if > 20 % of items are Blocked/Stuck
 *
 * Insights are plain-English strings surfaced in the BoardHealth widget.
 */

import type { MondayItem, BoardHealthMetrics } from '@core/types';
import { columnMap } from '@core/api/queries';

// ─── Constants ────────────────────────────────────────────────────────────────

const BLOCKED_STATUSES = new Set(['Blocked', 'Stuck']);
const DONE_STATUSES = new Set(['Done', 'Complete', 'Completed']);

const MS_PER_DAY = 1_000 * 60 * 60 * 24;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isOverdue(item: MondayItem, now: Date): boolean {
  const due = item.columnValues?.due_date;
  if (!due) return false;
  const d = new Date(due);
  if (isNaN(d.getTime())) return false;
  const status = item.columnValues?.status ?? '';
  if (DONE_STATUSES.has(status)) return false;
  return d.getTime() < now.getTime();
}

function daysOverdue(item: MondayItem, now: Date): number {
  const due = item.columnValues?.due_date;
  if (!due) return 0;
  const d = new Date(due);
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, (now.getTime() - d.getTime()) / MS_PER_DAY);
}

// ─── Main export ──────────────────────────────────────────────────────────────

/**
 * Compute health metrics for a board given its items.
 *
 * @param boardId   - ID of the board (passed through to the result)
 * @param boardName - Display name
 * @param items     - All items on the board
 * @param now       - Reference date (injectable for unit tests)
 */
export function computeBoardHealth(
  boardId: string,
  boardName: string,
  rawItems: MondayItem[],
  now: Date = new Date(),
): BoardHealthMetrics {
  // Enrich raw items: ensure columnValues flat map is populated so
  // helpers like isOverdue() can read item.columnValues.status etc.
  const items = rawItems.map((item) =>
    item.columnValues && Object.keys(item.columnValues).length > 0
      ? item
      : { ...item, columnValues: columnMap(item) },
  );

  const total = items.length;

  if (total === 0) {
    return {
      boardId,
      boardName,
      totalItems: 0,
      overdueCount: 0,
      blockedCount: 0,
      doneCount: 0,
      inProgressCount: 0,
      healthScore: 100,
      insights: ['Board has no items yet.'],
      computedAt: now.toISOString(),
    };
  }

  // ── Counts ────────────────────────────────────────────────────────────────

  let overdueCount = 0;
  let blockedCount = 0;
  let escalatedCount = 0;
  let doneCount = 0;
  let inProgressCount = 0;
  let worstOverdueDays = 0;

  for (const item of items) {
    const status = item.columnValues?.status ?? '';

    if (DONE_STATUSES.has(status)) {
      doneCount++;
    } else if (status === 'In Progress') {
      inProgressCount++;
    }

    if (BLOCKED_STATUSES.has(status)) blockedCount++;
    if (status === 'Escalated') escalatedCount++;

    if (isOverdue(item, now)) {
      overdueCount++;
      const d = daysOverdue(item, now);
      if (d > worstOverdueDays) worstOverdueDays = d;
    }
  }

  // ── Score ─────────────────────────────────────────────────────────────────

  let score = 100;

  // Per-item deductions (capped to avoid going below 0 on a single item type)
  score -= Math.min(overdueCount * 2, 30);
  score -= Math.min(blockedCount * 5, 25);
  score -= Math.min(escalatedCount * 3, 15);

  // Flat deductions for systemic problems
  if (total > 0 && overdueCount / total > 0.3) score -= 10;
  if (total > 0 && blockedCount / total > 0.2) score -= 10;

  score = Math.max(0, Math.min(100, Math.round(score)));

  // ── Insights ──────────────────────────────────────────────────────────────

  const insights: string[] = [];

  if (overdueCount === 0 && blockedCount === 0) {
    insights.push('Everything is on track — no overdue or blocked items.');
  }

  if (overdueCount > 0) {
    insights.push(
      `${overdueCount} item${overdueCount !== 1 ? 's are' : ' is'} overdue` +
        (worstOverdueDays > 1
          ? `, the worst by ${Math.round(worstOverdueDays)} day${Math.round(worstOverdueDays) !== 1 ? 's' : ''}.`
          : '.'),
    );
  }

  if (blockedCount > 0) {
    insights.push(
      `${blockedCount} item${blockedCount !== 1 ? 's are' : ' is'} Blocked or Stuck.`,
    );
  }

  if (escalatedCount > 0) {
    insights.push(`${escalatedCount} escalated item${escalatedCount !== 1 ? 's' : ''} need attention.`);
  }

  const pendingCount = total - doneCount;
  const donePercent = total > 0 ? Math.round((doneCount / total) * 100) : 0;
  if (donePercent >= 80) {
    insights.push(`${donePercent}% of items are done — board is nearly complete.`);
  } else if (doneCount > 0) {
    insights.push(`${doneCount} of ${total} items done (${donePercent}%), ${pendingCount} remaining.`);
  }

  if (score >= 80) {
    insights.push('Board health is good.');
  } else if (score >= 50) {
    insights.push('Board health needs attention.');
  } else {
    insights.push('Board health is critical — immediate action recommended.');
  }

  return {
    boardId,
    boardName,
    totalItems: total,
    overdueCount,
    blockedCount,
    doneCount,
    inProgressCount,
    healthScore: score,
    insights,
    computedAt: now.toISOString(),
  };
}

// ─── Colour helper ────────────────────────────────────────────────────────────

/** Map a health score to a CSS variable / token name for theming */
export function healthScoreColour(score: number): 'success' | 'warning' | 'danger' {
  if (score >= 75) return 'success';
  if (score >= 45) return 'warning';
  return 'danger';
}
